import React from 'react';
import {
  IconDelete,
  IconImageSearch,
  IconImage,
  IconFeatured,
} from '@bynder/icons';
import {
  Button,
  List,
  Thumbnail,
  Card,
  Inspector,
  Box,
  token,
} from '@bynder/design-system';
import { Translate, useTranslate } from '@bynder/localization';
import { useGuideMutations } from '../../../../mutations/guides';
import {
  IMAGE_UPLOADER_REQUIRED_PERMISSIONS,
  ImageExtensions,
} from '../../../../constants/widgets';
import { Cover, CVAsset, CVFile, Guide, ModalType } from '../../../../types';
import { AssetType } from '../../../../types/assetTypes';
import { GuideCoverImageUploaderButton } from './GuideCoverImageUploaderButton';
import { useHasAllPermissions } from '../../../../helpers/hooks';
import { SettingsAccordion } from '../SettingsAccordion';
import { useModalStore } from '../../../../stores/modalStore';
import { InspectorTitleEditor } from '../InspectorTitleEditor';
import { InspectorThemesetEditor } from '../InspectorThemesetEditor';
import CropperKebabMenu from '../../../pages/CropperKebabMenu';
import {
  CroppedImage,
  Aspect,
  useCropper,
  resetImageCrop,
  isImageCropped,
} from '@bynder/react-image-cropper-lib';
import {
  ActiveSplits,
  useReleaseToggle,
} from '../../../../helpers/releaseToggle';

export const GuideSettings: React.FC<{ guide: Guide }> = ({ guide }) => {
  const { translate } = useTranslate();
  const { editGuideTitle: editGuideTitleMutation } = useGuideMutations(
    guide.id,
  );

  const handleEditGuideTitle = (newTitle: string) => {
    if (!newTitle) return;

    editGuideTitleMutation.mutate({ guideTitle: newTitle });
  };

  return (
    <>
      <InspectorTitleEditor
        testId="guideTitle"
        onEdit={handleEditGuideTitle}
        subText={translate('GUIDE_INSPECTOR_PANEL.GUIDE_TITLE')}
        propTitle={guide.title}
      />
      <Inspector.Divider />
      <InspectorThemesetEditor guideId={guide.id} />
      <Inspector.Divider />
      <SettingsAccordion
        thumbnail={<Thumbnail shape="square" icon={<IconImage />} />}
        subtext={translate('GUIDE_INSPECTOR_PANEL.GUIDE_COVER_SUBTEXT')}
        title={translate('GUIDE_INSPECTOR_PANEL.COVER_IMAGE')}
      >
        <GuideCoverEditor guideId={guide.id} guideCover={guide.cover} />
      </SettingsAccordion>
      <FeatureGuideToggle guideId={guide.id} isFeatured={guide.isFeatured} />
    </>
  );
};

const GuideCoverEditor: React.FC<{
  guideId: string;
  guideCover: Cover;
}> = ({ guideId, guideCover }) => {
  const openModal = useModalStore.use.openModal();
  const canUseUploader = useHasAllPermissions(
    IMAGE_UPLOADER_REQUIRED_PERMISSIONS,
  );
  const { imageUrl } = guideCover;

  const {
    setGuideCover: setGuideCoverMutation,
    removeGuideCover: removeGuideCoverMutation,
  } = useGuideMutations(guideId);

  const toggleSetGuideCoverModalOpen = () =>
    openModal({
      modalType: ModalType.COMPACT_VIEW_MODAL,
      modalOnSubmit: handleChangeGuideCover,
      modalProps: {
        compactViewProps: {
          assetTypes: [AssetType.IMAGE],
        },
      },
    });

  const clearGuideCover = () => {
    removeGuideCoverMutation.mutate({ guideId });
  };

  const handleChangeGuideCover = (selectedMedia: CVAsset[], file: CVFile) => {
    if (selectedMedia.length) {
      const { databaseId, name, url, extensions, derivatives } =
        selectedMedia[0];
      let fileUrl;

      if (!(extensions[0] in ImageExtensions)) {
        fileUrl = derivatives.webImage;
      } else {
        fileUrl = file.selectedFile.url;
      }

      setGuideCoverMutation.mutate({
        guideCover: {
          assetId: databaseId,
          assetName: name,
          detailUrl: url,
          imageUrl: fileUrl,
        },
      });
    }
  };

  const onCropGuideCover = (croppedUrl: string) => {
    setGuideCoverMutation.mutate({
      guideCover: {
        ...guideCover,
        imageUrl: croppedUrl,
      },
    });
  };

  const onResetCrop = () => {
    setGuideCoverMutation.mutate({
      guideCover: {
        ...guideCover,
        imageUrl: resetImageCrop(guideCover.imageUrl),
      },
    });
  };

  const openCropper = useCropper();
  const oldContextAction = (
    <Card.ContextActionButton
      tooltip="Clear image"
      aria-label="Clear image"
      onClick={clearGuideCover}
    >
      <IconDelete />
    </Card.ContextActionButton>
  );

  const newContextAction = (
    <CropperKebabMenu
      actions={[
        {
          for: 'CROP',
          onClick: () =>
            openCropper({
              aspect: Aspect.COVER,
              src: imageUrl,
              onSuccess: croppedImage => {
                onCropGuideCover(croppedImage);
              },
            }),
        },
        isImageCropped(imageUrl) && {
          for: 'RESET_CROP',
          onClick: () => {
            onResetCrop();
          },
        },
        {
          for: 'CLEAR_IMAGE',
          onClick: clearGuideCover,
        },
      ]}
    />
  );

  const { data: isCropperSplitActive } = useReleaseToggle(
    ActiveSplits.imageCropperRollout,
  );

  const contextAction = isCropperSplitActive
    ? newContextAction
    : oldContextAction;

  return imageUrl ? (
    <Card
      variant="clean"
      onClick={toggleSetGuideCoverModalOpen}
      contextAction={contextAction}
    >
      <Card.Frame>
        {isCropperSplitActive ? (
          <CroppedImage src={imageUrl} absolutePositioning />
        ) : (
          <Card.Images>
            <Card.Img src={imageUrl} />
          </Card.Images>
        )}
      </Card.Frame>
      <Card.Header title={guideCover.assetName} />
    </Card>
  ) : (
    <Box paddingBottom="2">
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: token.spacing3,
        }}
      >
        <Button
          icon={<IconImageSearch />}
          variant="secondary"
          isFullWidth
          onClick={toggleSetGuideCoverModalOpen}
        >
          <Translate id="PAGE_INSPECTOR.DROPDOWN_COMPACT_VIEW" />
        </Button>
        <GuideCoverImageUploaderButton canUseUploader={canUseUploader} />
      </div>
    </Box>
  );
};

const FeatureGuideToggle: React.FC<{
  guideId: string;
  isFeatured: boolean;
}> = ({ guideId, isFeatured }) => {
  const {
    featureGuide: featureGuideMutation,
    unFeatureGuide: unFeatureGuideMutation,
  } = useGuideMutations(guideId);

  const handleFeatureGuide = () => {
    return isFeatured
      ? unFeatureGuideMutation.mutate()
      : featureGuideMutation.mutate();
  };

  return (
    <List.Item
      thumbnail={<Thumbnail shape="square" icon={<IconFeatured />} />}
      data-testid={isFeatured ? 'unFeatureGuide' : 'featureGuide'}
      checkedVariant="switch"
      isChecked={isFeatured}
      onClick={handleFeatureGuide}
    >
      <Translate id="GUIDE_INSPECTOR_PANEL.GUIDE_FEATURED" />
    </List.Item>
  );
};
